import "./HeaderBuilderScreen.scss";

import React, { useEffect, useState } from "react";
import { RhimButton, RhimDialog } from "@rhim/rhim-ui";
import { conditional, getMenuItems, getNextMenu } from "./HeaderBuilderUtil";
import { useLocation, useNavigate } from "react-router-dom";
import { useStoreActions, useStoreState } from "../../../../store/hooks";

import { FormName } from "../../atoms";
import FormSavingStatus from "../../atoms/FormSavingStatus";
import Grid from "@material-ui/core/Grid";
import Icon from "@material-ui/core/Icon";
import { MenuItem } from "../../molecules/SecondaryMenu/SecondaryMenu";
import { ModalType } from "../../../../feature/modal/enum/ModalType";
import { SecondaryMenu } from "../../molecules";
import UpdateStatus from "../../atoms/UpdateStaus";
import { cleanFormAutosave } from "../../../../feature/form/FormAutoSaveUtil";
import { update } from "lodash";
import { useModalVisibility } from "../../../../feature/modal";
import { useTranslation } from "react-i18next";

const HeaderBuilderScreen = () => {
  const { t } = useTranslation();
  const { modalProperties, setModalProperties } = useModalVisibility({
    visible: false
  });
  const navigate = useNavigate();
  const menuItems = getMenuItems(t);
  const [isInAssociationPage, setIsInUsersAssociationPage] = useState(false);
  const location = useLocation();

  const form = useStoreState((state) => state.form.form!);
  const save = useStoreActions((actions) => actions.form.save);
  const clearDebounce = useStoreActions((actions) => actions.form.clearDebounce);

  const selectForm = useStoreActions((actions) => async () => {
    await actions.form.selectForm({ form, t });
  });
  const notification = useStoreActions((actions) => actions.notification);

  const saveForm = async () => {
    await save({ form, t });
  };

  const isFormSaved = useStoreState((state) => state.form.isSaved);
  const updateIsSaved = useStoreActions((actions) => actions.form.updateIsSaved);

  useEffect(() => {
    setIsInUsersAssociationPage(location.pathname === `/Builder/UsersAssociation/${form.formId}`);
  }, [location]);

  const goTo = (menu: MenuItem) => {
    navigate(menu.path);
  };

  const renderModalMessage = () => {
    return <p className="modal-text">{t("messages.confirm_rules")}</p>;
  };

  const handleCancel = () => {
    const { nextIndex } = getNextMenu(menuItems);
    const index = nextIndex + (1 % menuItems.length);
    goTo(menuItems[index]);
  };

  const handleConfirm = () => {
    const { next } = getNextMenu(menuItems);
    goTo(next);
  };

  const handleRestore = () => {
    try {
      clearDebounce();
      if (cleanFormAutosave(form.formId)) {
        updateIsSaved(true);
      } 
      selectForm().then(() => {
        notification.setProperties({
          message: t("messages.restore_success"),
          title: t("messages.restore_success_title"),
          confirmText: t("common.ok").toUpperCase(),
          type: ModalType.Success,
          visible: true
        });
      });
    } catch (error: any) {
      notification.setProperties({
        confirmText: t("common.ok").toUpperCase(),
        message:
          error?.response?.data?.message !== ""
            ? t(error?.response?.data?.message)
            : t("messages.error_support"),
        title: `${t("messages.oops")}`,
        type: ModalType.Error,
        visible: true
      });
    }
  };

  const openModal = () => setModalProperties({ visible: true });

  const navigateWithCondition = (menu: MenuItem, condition: (menu: MenuItem) => boolean) =>
    conditional(menu, condition, goTo, openModal);

  const handleContinueClick = () => {
    const { next } = getNextMenu(menuItems);
    const rulesIsSelected = (menu: MenuItem) => menu.path !== "/Builder/Rules";
    navigateWithCondition(next, rulesIsSelected);
  };

  return (
    <header className="header-builder-screen">
      <div className="sub-menu">
        <div className="form-info-and-header-menu">
          <div className="form-name-and-header-menu">
            <FormName />
            <div className="menu">
              <SecondaryMenu menuItems={menuItems} navigate={goTo} />
            </div>
          </div>
          <UpdateStatus date={form.updatedAt!} email={form.updatedBy!} />
          <div className="form-satuts">
            <FormSavingStatus isSaved={isFormSaved} />
          </div>
        </div>
      </div>

      <div className="action-buttons">
        <Grid
          container
          direction="row"
          justifyContent="flex-end"
          alignItems="flex-start"
          spacing={2}>
          <Grid item>
            {!isFormSaved && (
              <div className="item">
                <RhimButton
                  variant="contained"
                  title={t("my_forms.delete_changes").toUpperCase()}
                  onClick={handleRestore}
                />
                <div className="tooltip-header tooltip-header-delete">
                  <span className="tooltip-header-text">{t("messages.autosave_info")}</span>
                </div>
              </div>
            )}
          </Grid>
          {!isInAssociationPage && (
            <Grid item>
              <RhimButton
                variant="outlined"
                icon={<Icon>arrow_forward</Icon>}
                iconSide="right"
                title={t("common.continue").toUpperCase()}
                onClick={
                  isInAssociationPage
                    ? async () => {
                        await saveForm();
                      }
                    : handleContinueClick
                }
              />
            </Grid>
          )}
          <Grid item>
            <RhimButton
              variant="contained"
              title={
                isInAssociationPage
                  ? t("users.finish").toUpperCase()
                  : t("common.save").toUpperCase()
              }
              onClick={async () => {
                await saveForm();
                if (isInAssociationPage) {
                  navigate("/MyForms");
                }
              }}
            />
          </Grid>
        </Grid>
      </div>

      <RhimDialog
        isMobile={false}
        title={<p className="modal-title">{t("pages.rules")}</p>}
        isOpen={modalProperties.visible}
        confirm={{
          title: t("pages.rules").toUpperCase(),
          handleFunction: () => {
            handleConfirm();
            setModalProperties({ visible: !modalProperties.visible });
          }
        }}
        cancel={{
          title: t("common.skip").toUpperCase(),
          handleFunction: () => {
            handleCancel();
            setModalProperties({ visible: !modalProperties.visible });
          }
        }}
        content={<p className="modal-text">{renderModalMessage()}</p>}
      />
    </header>
  );
};

export default HeaderBuilderScreen;
